import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createCase, updateCase, getCase } from '../../services/caseService';
import { getPatient } from '../../services/patientService';

const CaseForm = () => {
  const { patientId, caseId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isEditMode = Boolean(caseId);

  const [patient, setPatient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    treatmentGoals: '',
    status: 'active'
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userProfile && patientId) {
      loadPatient();
      if (isEditMode) {
        loadCase();
      }
    }
  }, [patientId, caseId, userProfile]);

  const loadPatient = async () => {
    try {
      const patientData = await getPatient(userProfile.practiceId, userProfile.id, patientId);
      setPatient(patientData);
    } catch (err) {
      console.error('Error loading patient:', err);
      setError('Failed to load patient information');
    }
  };

  const loadCase = async () => {
    try {
      setLoadingData(true);
      const caseData = await getCase(userProfile.practiceId, userProfile.id, patientId, caseId);
      setFormData({
        name: caseData.name || '',
        description: caseData.description || '',
        treatmentGoals: caseData.treatmentGoals || '',
        status: caseData.status || 'active'
      });
    } catch (err) {
      console.error('Error loading case:', err);
      setError('Failed to load case data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Case name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await updateCase(
          userProfile.practiceId,
          userProfile.id,
          patientId,
          caseId,
          formData
        );
      } else {
        await createCase(
          userProfile.practiceId,
          userProfile.id,
          patientId,
          formData
        );
      }

      navigate(`/patients/${patientId}`);
    } catch (err) {
      console.error('Error saving case:', err);
      setError('Failed to save case. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/patients/${patientId}`);
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Treatment Case' : 'New Treatment Case'}
        </h1>
        {patient && (
          <p className="mt-1 text-sm text-gray-600">
            For patient: {patient.firstName} {patient.lastName}
          </p>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Case Information
          </h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Case Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="name"
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                placeholder="e.g., Full Smile Makeover 2024"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                id="status"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
                placeholder="Brief description of the treatment case"
              />
            </div>

            <div>
              <label htmlFor="treatmentGoals" className="block text-sm font-medium text-gray-700">
                Treatment Goals
              </label>
              <textarea
                name="treatmentGoals"
                id="treatmentGoals"
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={formData.treatmentGoals}
                onChange={handleChange}
                disabled={loading}
                placeholder="• Improve smile aesthetics&#10;• Correct bite alignment&#10;• Enhance function"
              />
            </div>
          </div>
        </div>

        {/* Treatment Phases Info */}
        {!isEditMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Treatment Phases
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    This case will include 4 treatment phases:
                  </p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Initial - Starting documentation</li>
                    <li>Post-Ortho - After orthodontic work</li>
                    <li>Provisional - Temporary restorations</li>
                    <li>Final - Completed treatment</li>
                  </ul>
                  <p className="mt-2">
                    Each phase will have its own photo requirements based on the Dawson 21 protocol.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              <span>{isEditMode ? 'Update Case' : 'Create Case'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CaseForm;
